import { BarData, BusinessDay, LineData, Time, isBusinessDay } from 'lightweight-charts';

import { EChartPeriod, IMarketChartData } from '@aqx-core/common/api/chart';
import dayjs from '@aqx-core/common/dayjs';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { destructureAsNumber } from '@aqx-core/common/utils/number';

import { ICustomBarData } from '../types/chart';
import { EChartLineSeriesDataType } from '../types/series';

export interface IPeriodToDatesParams {
  period: EChartPeriod;
  latestTo?: number;
  latestFrom?: number;
  dateToUsesEndOfDay?: boolean;
}

// Separate functions from dayjs core library
export const timeToDayjs = (time?: Time) => {
  const offset = companyViewController.getUTCOffsetForChart() ?? 0;

  return dayjs(time ? timeToDate(time) : undefined).utcOffset(offset);
};

export const timeToDate = (time: Time) => {
  if (isBusinessDay(time)) {
    const date = time as BusinessDay;

    return new Date(date.year, date.month, date.day);
  }

  if (typeof time === 'string') {
    return new Date(time);
  }

  return new Date(time * 1000);
};

export const rawToBarData = (chartData: IMarketChartData[]) => {
  const barData: ICustomBarData[] = [];

  chartData.forEach((d) => {
    const { chartTime, ask, open, close, high, low } = destructureAsNumber(d, [
      'chartTime',
      'ask',
      'open',
      'close',
      'high',
      'low'
    ]);

    barData.push({
      time: (chartTime / 1000) as Time,
      ask,
      open,
      close,
      high,
      low
    });
  });

  return barData;
};

export const barToLineData = (barData: BarData): LineData => {
  return {
    time: barData.time,
    value: barData.close
  };
};

export const barToLineSeriesData = (dataType: EChartLineSeriesDataType, barData: ICustomBarData): LineData => {
  return {
    time: barData.time,
    value: dataType === EChartLineSeriesDataType.ASK ? barData.ask ?? 0 : barData.close
  };
};

// eslint-disable-next-line complexity
export const periodToDates = (params: IPeriodToDatesParams) => {
  const { period, latestTo, latestFrom, dateToUsesEndOfDay } = params;

  const to = dateToUsesEndOfDay
    ? timeToDayjs(latestTo as Time)
        .endOf('day')
        .toDate()
        .getTime()
    : timeToDayjs(latestTo as Time)
        .toDate()
        .getTime();
  const from = timeToDayjs(latestFrom as Time);

  switch (period) {
    case EChartPeriod.PERIOD_M5:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.PERIOD_M10:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.PERIOD_M15:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
    case EChartPeriod.PERIOD_M20:
      return {
        to,
        from: from.subtract(7, 'days').toDate().getTime()
      };
    case EChartPeriod.PERIOD_M30:
      return {
        to,
        from: from.subtract(7, 'days').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H1:
      return {
        to,
        from: from.subtract(1, 'months').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H2:
      return {
        to,
        from: from.subtract(2, 'months').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H3:
      return {
        to,
        from: from.subtract(3, 'months').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H4:
      return {
        to,
        from: from.subtract(4, 'months').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H6:
      return {
        to,
        from: from.subtract(6, 'months').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H8:
      return {
        to,
        from: from.subtract(1, 'year').toDate().getTime()
      };
    case EChartPeriod.PERIOD_H12:
      return {
        to,
        from: from.subtract(1, 'year').toDate().getTime()
      };
    case EChartPeriod.PERIOD_D1:
      return {
        to,
        from: from.subtract(1, 'years').toDate().getTime()
      };
    case EChartPeriod.PERIOD_W1:
      return {
        to,
        from: from.subtract(6, 'years').toDate().getTime()
      };
    case EChartPeriod.PERIOD_MN1:
      return {
        to,
        from: from.subtract(12, 'years').toDate().getTime()
      };
    default:
      return {
        to,
        from: from.subtract(3, 'days').toDate().getTime()
      };
  }
};

export const trimBarData = (barData: ICustomBarData[], limit: number) => {
  const trimmedBarData = [...barData];
  let filteredData = false;
  while (!filteredData) {
    const newLastData = trimmedBarData[trimmedBarData.length - 1];
    if (!newLastData) {
      filteredData = true;
      break;
    }
    if (Number(newLastData.time) >= limit) {
      trimmedBarData.pop();
    } else {
      filteredData = true;
    }
  }
  return trimmedBarData;
};
